import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useState, useEffect, Component, ErrorInfo, ReactNode } from "react";
import { AnimatePresence } from "framer-motion";
import { AuthProvider } from "@/hooks/useAuth";
import ProtectedRoute from "@/components/ProtectedRoute";
import Index from "./pages/Index";
import AboutPage from "./pages/AboutPage";
import ServicesPage from "./pages/ServicesPage";
import WebDevelopmentPage from "./pages/WebDevelopmentPage";
import DigitalMarketingPage from "./pages/DigitalMarketingPage";
import VideoEditingPage from "./pages/VideoEditingPage";
import VideoAdsPage from "./pages/VideoAdsPage";
import InfluencerMarketingPage from "./pages/InfluencerMarketingPage";
import SocialMediaPage from "./pages/SocialMediaPage";
import AnimationPage from "./pages/AnimationPage";
import DesignPage from "./pages/DesignPage";
import ProjectsPage from "./pages/ProjectsPage";
import ContactPage from "./pages/ContactPage";
import CareersPage from "./pages/CareersPage";
import QuotePage from "./pages/QuotePage";
import MukeshAdminPage from "./pages/MukeshAdminPage";
import NotFound from "./pages/NotFound";
import SplashScreen from "./components/SplashScreen";
import BackToTopButton from "./components/BackToTopButton";
import FloatingSocialButtons from "./components/FloatingSocialButtons";
import MobileNavBar from "./components/MobileNavBar";
import AIChatbot from "./components/AIChatbot";

const queryClient = new QueryClient();

// Splash screen state management
const hasSplashScreenShown = () => {
  return localStorage.getItem('splashScreenShown') === 'true';
};

const setSplashScreenShown = () => {
  localStorage.setItem('splashScreenShown', 'true');
};

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// Error Boundary Component
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
            <p className="text-gray-600 mb-4">Please try refreshing the page. If the problem persists, contact support.</p>
            <pre className="bg-gray-100 p-4 rounded overflow-auto text-sm">
              {this.state.error?.toString()}
            </pre>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const App = () => {
  const [showSplash, setShowSplash] = useState(!hasSplashScreenShown());
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleSplashFinish = () => {
    setShowSplash(false);
    setSplashScreenShown();
  };

  if (showSplash) {
    return <SplashScreen onFinish={handleSplashFinish} />;
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <TooltipProvider>
              <AnimatePresence mode="wait">
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/services" element={<ServicesPage />} />
                  <Route path="/services/web-development" element={<WebDevelopmentPage />} />
                  <Route path="/services/digital-marketing" element={<DigitalMarketingPage />} />
                  <Route path="/services/video-editing" element={<VideoEditingPage />} />
                  <Route path="/services/video-ads" element={<VideoAdsPage />} />
                  <Route path="/services/influencer-marketing" element={<InfluencerMarketingPage />} />
                  <Route path="/services/social-media" element={<SocialMediaPage />} />
                  <Route path="/services/animation" element={<AnimationPage />} />
                  <Route path="/services/design" element={<DesignPage />} />
                  <Route path="/projects" element={<ProjectsPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/careers" element={<CareersPage />} />
                  <Route path="/quote" element={<QuotePage />} />
                  <Route
                    path="/mukesh"
                    element={
                      <ProtectedRoute>
                        <MukeshAdminPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </AnimatePresence>
              <BackToTopButton />
              <FloatingSocialButtons />
              <AIChatbot />
              {isMobile && <MobileNavBar />}
              <Toaster />
              <Sonner />
            </TooltipProvider>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
